import React, { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Download, Info, CheckCircle2, AlertTriangle, XCircle, RefreshCcw } from "lucide-react";
import { useLogStore } from "@/stores/log.store";
import { cn } from "@/lib/utils";

export const LogPanel: React.FC = () => {
  const { logs, clearLogs } = useLogStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [logs]);

  const getIcon = (level: string) => {
    switch (level) {
      case 'info': return <Info className="w-3.5 h-3.5 text-blue-500" />;
      case 'success': return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
      case 'warn': return <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />;
      case 'error': return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      case 'progress': return <RefreshCcw className="w-3.5 h-3.5 text-purple-500 animate-spin" />;
      default: return <Info className="w-3.5 h-3.5 text-muted-foreground" />;
    }
  };

  return (
    <div className="h-full flex flex-col bg-muted/5 font-mono">
      <div className="h-9 border-b flex items-center justify-between px-3 bg-muted/20">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">实时日志</span>
          <Badge variant="secondary" className="h-4 px-1 text-[10px] tabular-nums">
            {logs.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={clearLogs}>
            <Trash2 className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6">
            <Download className="w-3 h-3" />
          </Button>
        </div>
      </div>
      
      <ScrollArea ref={scrollRef} className="flex-1">
        <div className="p-2 space-y-0.5">
          {logs.length === 0 ? (
            <div className="text-center py-8 text-xs text-muted-foreground italic">
              暂无日志记录
            </div>
          ) : (
            logs.map((log) => (
              <div 
                key={log.id} 
                className={cn(
                  "flex items-start gap-2 py-0.5 px-2 rounded-sm text-[11px] leading-relaxed",
                  "hover:bg-muted/30 transition-colors",
                  log.level === 'error' && "bg-red-500/5 text-red-900 dark:text-red-200",
                  log.level === 'success' && "text-green-700 dark:text-green-300"
                )}
              >
                <span className="text-muted-foreground shrink-0 tabular-nums">
                  [{log.timestamp.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]
                </span>
                <span className="shrink-0 mt-0.5">{getIcon(log.level)}</span>
                <span className="break-all whitespace-pre-wrap">{log.message}</span>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
